import React, { createContext, useContext, useState, useEffect } from "react";

// Translation data
const translations = {
  en: {
    // Header
    howItWorks: "How it works",
    features: "Features",
    pricing: "Pricing",
    login: "Login",
    startFreeTrial: "Start Free Trial",
    dashboard: "Dashboard",
    logout: "Logout",
    
    // Hero
    aiVideoIntelligence: "AI Video Intelligence",
    heroTitle1: "Every word.",
    heroTitle2: "Every moment.",
    heroTitle3: "Understood.",
    heroDescription: "Upload any video. Voxara converts it to audio, transcribes with AI precision, and pinpoints the key moments that matter — timestamped and explained.",
    heroDescriptionIntro: "Ideal for virtual meetings or educational content.",
    heroDescriptionHighlight: "Turn 5-hour recordings into 10-minute summaries.",
    heroDescriptionOutro: "Search the content and generate professional AI reports.",
    uploadYourVideo: "Upload Your Video",
    watchDemo: "Watch Demo",
    noCreditCard: "// No credit card required · Cancel anytime",
    
    // Audio Processing Card
    audioProcessing: "Audio Processing",
    analyzing: "Analyzing",
    keyInsightDetected: "Key insight detected",
    
    // How it works
    howVoxaraWorks: "How Voxara works",
    step01: "Step 01",
    step02: "Step 02",
    step03: "Step 03",
    step04: "Step 04",
    step05: "Step 05",
    uploadVideo: "Upload Video / Audio",
    uploadVideoDesc: "Drop in any video or audio file — MP4, MOV, AVI, WebM, MP3, WAV and more. Any length, any language.",
    audioExtraction: "Optimized Processing",
    audioExtractionDesc: "We convert and optimize your video or audio to speed up transcription and deliver results faster.",
    aiTranscription: "AI Transcription",
    aiTranscriptionDesc: "State-of-the-art speech models transcribe every word with speaker detection and timestamps.",
    keyMomentAnalysis: "Key Moment Analysis",
    keyMomentAnalysisDesc: "AI reads full context and surfaces the most important timestamps — with reasoning for each.",
    talkToYourContent: "Talk to Your Content",
    talkToYourContentDesc: "Ask anything you need about your video or audio and let AI find answers, key moments, and external sources in seconds. Turn hours of content into minutes of useful work.",
    
    // Features
    builtForSeriousWork: "Built for serious work",
    builtForTitle1: "Built for",
    builtForTitle2: "serious work",
    intelligence: "Intelligence",
    contextualKeyMoments: "Contextual Key Moments",
    contextualKeyMomentsDesc: "Not just timestamps — AI explains why each moment matters. Decisions, tone shifts, critical data points.",
    performance: "Performance",
    audioFirstPipeline: "Audio-First Pipeline",
    audioFirstPipelineDesc: "We extract audio before any analysis, cutting processing time and memory dramatically.",
    transcription: "Transcription",
    fullSearchableTranscripts: "Full, Searchable Transcripts",
    fullSearchableTranscriptsDesc: "Every word, every speaker, perfectly timed. Export to TXT, SRT, or JSON.",
    languages: "Languages",
    fortyPlusLanguages: "40+ Languages",
    fortyPlusLanguagesDesc: "Auto-detection with support for over 40 languages. Multi-speaker diarization available.",
    library: "Library",
    videoHistorySearch: "Video History & Search",
    videoHistorySearchDesc: "Every analysis saved forever. Search across all your transcripts instantly.",
    collaboration: "Collaboration",
    shareExport: "Share & Export",
    shareExportDesc: "One-click sharing with your team. Export in all formats. Embed summaries anywhere.",
    
    // Stats
    accuracy: "Accuracy",
    processing: "Processing",
    
    // Pricing
    pricingTitle1: "Simple,",
    pricingTitle2: "honest pricing",
    simpleHonestPricing: "Simple, honest pricing",
    starter: "Starter",
    professional: "Professional",
    pro: "Pro",
    enterprise: "Enterprise",
    mostPopular: "Most Popular",
    perMonth: "per month, billed monthly",
    perUserMonth: "per user/month",
    getStarted: "Get Started",
    freeTrialNote: "// 7-day free trial on all plans · No credit card required",
    
    // Pricing features
    tenHoursDay: "10 hours of video / day",
    fifteenHoursDay: "15 hours of video / day",
    fiveHours: "5 hours of video / month",
    twentyFiveHours: "25 hours of video / month",
    unlimitedHours: "Unlimited video hours",
    fullTranscription: "Full transcription",
    keyMomentDetection: "Key moment detection",
    aiSummary: "AI Summary",
    exportTxtSrt: "Export TXT & SRT",
    exportAll: "Export TXT, SRT & JSON",
    allExportFormats: "All export formats",
    sevenDayHistory: "7-day history",
    unlimitedHistory: "Unlimited history",
    multiSpeaker: "Multi-speaker detection",
    apiAccess: "API access",
    apiThousand: "API (1,000 calls/mo)",
    unlimitedApi: "Unlimited API access",
    standardSupport: "Standard support",
    prioritySupport: "Priority support",
    premiumSupport: "Premium support",
    priorityProcessing: "Priority processing",
    detailedSummaries: "Detailed summaries",
    teamUse: "Team & firm use",
    adminControls: "Admin controls",
    
    // Professional tier description
    proDesc1: "Includes AI chat for videos, audio, documents, and PDFs",
    proDesc2: "Context-aware answers over long transcripts",
    proDesc3: "Priority processing and support",
    proDesc4: "Ideal for intensive work with videos, audio, and context",
    
    // Starter tier description
    starterDesc1: "10 hours per day across video, audio, documents, and PDFs",
    starterDesc2: "Includes AI chat for documents and PDFs",
    starterDesc3: "Does not include AI chat for videos and audio",
    starterDesc4: "Transcription, key moments, and AI summaries",
    starterDesc5: "Export to TXT, SRT, PDF, and Word",
    starterDesc6: "Standard support",
    starterDesc7: "Ideal for transcription and document work with clarity",
    starterDesc8: "Built for individual work with essential needs",
    
    // Enterprise tier description
    enterpriseDesc1: "Unlimited processing for high-volume operations",
    enterpriseDesc2: "Includes AI chat across all formats",
    enterpriseDesc3: "Admin controls and team usage",
    enterpriseDesc4: "Premium support",
    enterpriseDesc5: "Ideal for teams, firms, and large operations",
    enterpriseDesc6: "Maximum priority for critical workflows",
    enterpriseDesc7: "Export to TXT, SRT, PDF, and Word",
    enterpriseDesc8: "Export to TXT, SRT, PDF and Word",
    enterpriseDesc9: "Built to scale with order",
    enterpriseDesc10: "Admin controls to scale with order",
    enterpriseDesc11: "High-priority premium support",
    
    // Footer
    copyright: "© 2025 Voxara. AI Video Transcription.",
    
    // Dashboard
    dashboardTitle: "Dashboard",
    dashboardSubtitle: "Upload and manage your video transcriptions",
    refresh: "Refresh",
    yourVideos: "Your Videos",
    video: "video",
    videos: "videos",
    noVideosYet: "No videos yet",
    noVideosDesc: "Upload your first video to get started with AI transcription",
    
    // Upload
    dropVideoHere: "Drop your video here",
    orClickToBrowse: "Or click to browse",
    selectVideo: "Select Video",
    uploadAndTranscribe: "Upload & Transcribe",
    uploading: "Uploading...",
    
    // Video Status
    pending: "Pending",
    extractingAudio: "Extracting Audio",
    transcribing: "Transcribing",
    analyzingStatus: "Analyzing",
    completed: "Completed",
    failed: "Failed",
    
    // Video Detail
    backToDashboard: "Back to Dashboard",
    export: "Export",
    exportAsTxt: "Export as TXT",
    exportAsSrt: "Export as SRT",
    processingFailed: "Processing Failed",
    keyMoments: "Key Moments",
    detected: "detected",
    fullTranscriptTitle: "Full Transcript",
    noTranscript: "No transcript available",
    noKeyMoments: "No key moments detected",
    
    // Auth
    welcomeBack: "Welcome back",
    signInAccess: "Access your transcripts",
    email: "Email",
    password: "Password",
    forgotPassword: "Forgot password?",
    signIn: "Sign In",
    signingIn: "Signing in...",
    noAccount: "Don't have an account?",
    signUp: "Sign up",
    
    createAccount: "Create your account",
    startTranscribing: "Start transcribing videos in seconds",
    username: "Username",
    confirmPassword: "Confirm Password",
    createAccountBtn: "Create Account",
    creatingAccount: "Creating account...",
    haveAccount: "Already have an account?",
    
    resetPassword: "Reset Password",
    enterEmailReset: "Enter your email to receive a reset code",
    enterCode: "Enter the 6-digit code sent to your email",
    createNewPassword: "Create a new password",
    sendResetCode: "Send Reset Code",
    sendingCode: "Sending code...",
    verifyCode: "Verify Code",
    back: "Back",
    newPassword: "New Password",
    confirmNewPassword: "Confirm New Password",
    resetPasswordBtn: "Reset Password",
    resettingPassword: "Resetting password...",
    rememberPassword: "Remember your password?",
  },
  es: {
    // Header
    howItWorks: "Cómo funciona",
    features: "Características",
    pricing: "Precios",
    login: "Iniciar sesión",
    startFreeTrial: "Prueba Gratis",
    dashboard: "Panel",
    logout: "Cerrar sesión",
    
    // Hero
    aiVideoIntelligence: "Inteligencia de Video/Audio IA",
    heroTitle1: "Resume horas de video/audio/texto",
    heroTitle2: "Mejora tu productividad",
    heroTitle3: "con el analisis inteligente de Voxara.",
    heroDescription: "Ideal para reuniones virtuales o contenido educativo. Transforma grabaciones de 5 horas en resúmenes en 10 minutos, consulta el contenido y genera reportes profesionales con IA.",
    heroDescriptionIntro: "Ideal para reuniones virtuales o contenido educativo.",
    heroDescriptionHighlight: "Transforma grabaciones de 5 horas en resúmenes en 10 minutos.",
    heroDescriptionOutro: "Consulta el contenido y genera reportes profesionales con IA.",
    uploadYourVideo: "Sube Tu Video",
    watchDemo: "Ver Demo",
    noCreditCard: "// Sin tarjeta de crédito · Cancela cuando quieras",
    
    // Audio Processing Card
    audioProcessing: "Procesamiento de Audio",
    analyzing: "Analizando",
    keyInsightDetected: "Información clave detectada",
    
    // How it works
    howVoxaraWorks: "Cómo funciona Voxara",
    step01: "Paso 01",
    step02: "Paso 02",
    step03: "Paso 03",
    step04: "Paso 04",
    step05: "Paso 05",
    uploadVideo: "Subir Video / Audio",
    uploadVideoDesc: "Arrastra cualquier archivo de video o audio — MP4, MOV, AVI, WebM, MP3, WAV y más. Cualquier duración, cualquier idioma.",
    audioExtraction: "Procesamiento Optimizado",
    audioExtractionDesc: "Convertimos y optimizamos tu video o audio para acelerar la transcripción y entregarte resultados más rápido.",
    aiTranscription: "Transcripción IA",
    aiTranscriptionDesc: "Modelos de voz de última generación transcriben cada palabra con detección de hablantes y marcas de tiempo.",
    keyMomentAnalysis: "Análisis de Momentos Clave",
    keyMomentAnalysisDesc: "La IA lee todo el contexto y resalta las marcas de tiempo más importantes — con razonamiento para cada una.",
    talkToYourContent: "Habla con tu contenido",
    talkToYourContentDesc: "Pregunta lo que necesites sobre tu video o audio y deja que la IA encuentre respuestas, momentos clave y fuentes externas en segundos. Convierte horas de contenido en minutos de trabajo útil.",
    
    // Features
    builtForSeriousWork: "Construido para trabajo serio",
    builtForTitle1: "Construido para",
    builtForTitle2: "trabajo serio",
    intelligence: "Inteligencia",
    contextualKeyMoments: "Momentos Clave Contextuales",
    contextualKeyMomentsDesc: "No solo marcas de tiempo — la IA explica por qué cada momento importa. Decisiones, cambios de tono, datos críticos.",
    performance: "Rendimiento",
    audioFirstPipeline: "Pipeline Audio-Primero",
    audioFirstPipelineDesc: "Extraemos audio antes de cualquier análisis, reduciendo dramáticamente el tiempo y memoria de procesamiento.",
    transcription: "Transcripción",
    fullSearchableTranscripts: "Transcripciones Completas y Buscables",
    fullSearchableTranscriptsDesc: "Cada palabra, cada hablante, perfectamente sincronizado. Exporta a TXT, SRT o JSON.",
    languages: "Idiomas",
    fortyPlusLanguages: "40+ Idiomas",
    fortyPlusLanguagesDesc: "Detección automática con soporte para más de 40 idiomas. Diarización multi-hablante disponible.",
    library: "Biblioteca",
    videoHistorySearch: "Historial y Búsqueda de Videos",
    videoHistorySearchDesc: "Cada análisis guardado para siempre. Busca en todas tus transcripciones instantáneamente.",
    collaboration: "Colaboración",
    shareExport: "Compartir y Exportar",
    shareExportDesc: "Comparte con un clic con tu equipo. Exporta en todos los formatos. Incrusta resúmenes en cualquier lugar.",
    
    // Stats
    accuracy: "Precisión",
    processing: "Procesamiento",
    
    // Pricing
    pricingTitle1: "Precios",
    pricingTitle2: "simples y honestos",
    simpleHonestPricing: "Precios simples y honestos",
    starter: "Inicial",
    professional: "Profesional",
    pro: "Pro",
    enterprise: "Empresa",
    mostPopular: "Más Popular",
    perMonth: "por mes, facturado mensualmente",
    perUserMonth: "por usuario/mes",
    getStarted: "Comenzar",
    freeTrialNote: "// 7 días de prueba gratis en todos los planes · Sin tarjeta de crédito",
    
    // Pricing features
    tenHoursDay: "10 horas de video / día",
    fifteenHoursDay: "15 horas de video / día",
    fiveHours: "5 horas de video / mes",
    twentyFiveHours: "25 horas de video / mes",
    unlimitedHours: "Horas de video ilimitadas",
    fullTranscription: "Transcripción completa",
    keyMomentDetection: "Detección de momentos clave",
    aiSummary: "Resumen con IA",
    exportTxtSrt: "Exportar TXT y SRT",
    exportAll: "Exportar TXT, SRT y JSON",
    allExportFormats: "Todos los formatos de exportación",
    sevenDayHistory: "Historial de 7 días",
    unlimitedHistory: "Historial ilimitado",
    multiSpeaker: "Detección multi-hablante",
    apiAccess: "Acceso API",
    apiThousand: "API (1,000 llamadas/mes)",
    unlimitedApi: "Acceso API ilimitado",
    standardSupport: "Soporte estándar",
    prioritySupport: "Soporte prioritario",
    premiumSupport: "Soporte premium",
    priorityProcessing: "Procesamiento prioritario",
    detailedSummaries: "Resúmenes detallados",
    teamUse: "Uso para equipos y firmas",
    adminControls: "Controles administrativos",
    
    // Professional tier description
    proDesc1: "Incluye chat con AI en videos, audios, documentos y PDF",
    proDesc2: "Respuestas con contexto sobre transcripciones largas",
    proDesc3: "Procesamiento y soporte prioritarios",
    proDesc4: "Ideal para trabajo intensivo con videos, audios y contexto",
    
    // Starter tier description
    starterDesc1: "10 horas al día para video, audio, documentos y PDF",
    starterDesc2: "Incluye chat con AI en documentos y PDF",
    starterDesc3: "No incluye chat con AI en videos ni audios",
    starterDesc4: "Transcripción, momentos clave y resúmenes con IA",
    starterDesc5: "Exporta en TXT, SRT, PDF y Word",
    starterDesc6: "Soporte estándar",
    starterDesc7: "Ideal para transcribir y trabajar documentos con claridad",
    starterDesc8: "Pensado para trabajo individual con necesidades básicas",
    
    // Enterprise tier description
    enterpriseDesc1: "Procesamiento ilimitado para operación de alto volumen",
    enterpriseDesc2: "Incluye chat con AI en todos los formatos",
    enterpriseDesc3: "Controles admin y uso para equipos",
    enterpriseDesc4: "Soporte premium",
    enterpriseDesc5: "Ideal para equipos, firmas y operaciones grandes",
    enterpriseDesc6: "Prioridad máxima en flujos críticos",
    enterpriseDesc7: "Exporta en TXT, SRT, PDF y Word",
    enterpriseDesc8: "Exporta en TXT, SRT, PDF y Word",
    enterpriseDesc9: "Diseñado para escalar con orden",
    enterpriseDesc10: "Controles administrativos para escalar con orden",
    enterpriseDesc11: "Soporte premium de alta prioridad",
    
    // Footer
    copyright: "© 2025 Voxara. Transcripción de Video con IA.",
    
    // Dashboard
    dashboardTitle: "Panel de Control",
    dashboardSubtitle: "Sube y administra tus transcripciones de video",
    refresh: "Actualizar",
    yourVideos: "Tus Videos",
    video: "video",
    videos: "videos",
    noVideosYet: "Sin videos aún",
    noVideosDesc: "Sube tu primer video para comenzar con la transcripción IA",
    
    // Upload
    dropVideoHere: "Arrastra tu video aquí",
    orClickToBrowse: "O haz clic para buscar",
    selectVideo: "Seleccionar Video",
    uploadAndTranscribe: "Subir y Transcribir",
    uploading: "Subiendo...",
    
    // Video Status
    pending: "Pendiente",
    extractingAudio: "Extrayendo Audio",
    transcribing: "Transcribiendo",
    analyzingStatus: "Analizando",
    completed: "Completado",
    failed: "Fallido",
    
    // Video Detail
    backToDashboard: "Volver al Panel",
    export: "Exportar",
    exportAsTxt: "Exportar como TXT",
    exportAsSrt: "Exportar como SRT",
    processingFailed: "Procesamiento Fallido",
    keyMoments: "Momentos Clave",
    detected: "detectados",
    fullTranscriptTitle: "Transcripción Completa",
    noTranscript: "Sin transcripción disponible",
    noKeyMoments: "Sin momentos clave detectados",
    
    // Auth
    welcomeBack: "Bienvenido de nuevo",
    signInAccess: "Accede a tus transcripciones",
    email: "Correo electrónico",
    password: "Contraseña",
    forgotPassword: "¿Olvidaste tu contraseña?",
    signIn: "Iniciar Sesión",
    signingIn: "Iniciando sesión...",
    noAccount: "¿No tienes cuenta?",
    signUp: "Regístrate",
    
    createAccount: "Crea tu cuenta",
    startTranscribing: "Comienza a transcribir videos en segundos",
    username: "Nombre de usuario",
    confirmPassword: "Confirmar Contraseña",
    createAccountBtn: "Crear Cuenta",
    creatingAccount: "Creando cuenta...",
    haveAccount: "¿Ya tienes cuenta?",
    
    resetPassword: "Restablecer Contraseña",
    enterEmailReset: "Ingresa tu correo para recibir un código",
    enterCode: "Ingresa el código de 6 dígitos enviado a tu correo",
    createNewPassword: "Crea una nueva contraseña",
    sendResetCode: "Enviar Código",
    sendingCode: "Enviando código...",
    verifyCode: "Verificar Código",
    back: "Volver",
    newPassword: "Nueva Contraseña",
    confirmNewPassword: "Confirmar Nueva Contraseña",
    resetPasswordBtn: "Restablecer Contraseña",
    resettingPassword: "Restableciendo contraseña...",
    rememberPassword: "¿Recuerdas tu contraseña?",
  },
};

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const storedLanguage = localStorage.getItem("voxara_language");
    const migratedToSpanishDefault = localStorage.getItem("voxara_language_default_es_migrated") === "true";

    if (!migratedToSpanishDefault) {
      localStorage.setItem("voxara_language_default_es_migrated", "true");
      localStorage.setItem("voxara_language", "es");
      return "es";
    }

    return storedLanguage || "es";
  });

  useEffect(() => {
    localStorage.setItem("voxara_language", language);
  }, [language]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.lang = language;
    document.documentElement.setAttribute("translate", "no");
    document.documentElement.classList.add("notranslate");
  }, [language]);

  const t = (key) => {
    return translations[language][key] || translations.en[key] || key;
  };

  const toggleLanguage = (lang) => {
    setLanguage(lang);
  };

  return (
    <LanguageContext.Provider value={{ language, t, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
